import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaAngleDown, FaCheck } from 'react-icons/fa';
import { useCurrency } from '../contexts/CurrencyContext';

const ACCENT = '#BA824F';

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 1200;
`;

const DropdownHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  background: #fff;
  border: 1px solid ${p => (p.$open ? ACCENT : '#e6e2dd')};
  border-radius: 999px;
  cursor: pointer;
  font-family: 'Archia', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #2b2b2b;
  letter-spacing: 0.2px;
  transition: border-color 0.2s, color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: ${ACCENT};
    color: ${ACCENT};
  }

  ${p => p.$open && `
    color: ${ACCENT};
    box-shadow: 0 0 0 3px rgba(186, 130, 79, 0.12);
  `}
`;

const SymbolText = styled.span`
  font-weight: 700;
  color: ${ACCENT};
`;

const Chevron = styled(FaAngleDown)`
  font-size: 14px;
  color: #9a938c;
  transition: transform 0.2s ease;
  transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
`;

const DropdownList = styled.ul`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 230px;
  max-height: 280px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #f0ece7;
  border-radius: 14px;
  box-shadow: 0 12px 32px rgba(43, 32, 22, 0.14);
  margin: 0;
  padding: 6px;
  z-index: 2000;
  list-style: none;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd5cc;
    border-radius: 3px;
  }
`;

const DropdownListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-family: 'Archia', sans-serif;
  transition: background 0.15s;

  &:hover {
    background: #faf5ef;
  }

  ${p => p.$selected && `
    background: rgba(186, 130, 79, 0.10);
  `}
`;

const ItemSymbol = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #f4efe9;
  color: ${ACCENT};
  font-size: 13px;
  font-weight: 700;
`;

const ItemText = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ItemIso = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #2b2b2b;
`;

const ItemName = styled.span`
  font-size: 12px;
  color: #9a938c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CheckIcon = styled(FaCheck)`
  font-size: 11px;
  color: ${ACCENT};
  flex-shrink: 0;
`;

const CurrencyDropdown = () => {
  const { currency, setCurrency, currenciesData } = useCurrency();
  const [currencies, setCurrencies] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  const isOpenRef = useRef(false);

  useEffect(() => {
    const ensureList = (list) => {
      const map = new Map(
        list
          .filter(m => m?.isoMOEDA && m?.simboloMOEDA && m?.tipoMOEDA)
          .map(m => [String(m.isoMOEDA).toUpperCase(), {
            isoMOEDA: String(m.isoMOEDA).toUpperCase(),
            simboloMOEDA: String(m.simboloMOEDA),
            tipoMOEDA: String(m.tipoMOEDA)
          }])
      );
      if (!map.has('EUR')) map.set('EUR', { isoMOEDA: 'EUR', simboloMOEDA: '€', tipoMOEDA: 'Euro' });
      if (!map.has('USD')) map.set('USD', { isoMOEDA: 'USD', simboloMOEDA: 'US$', tipoMOEDA: 'Dólar americano' });
      if (!map.has('GBP')) map.set('GBP', { isoMOEDA: 'GBP', simboloMOEDA: '£', tipoMOEDA: 'Libra esterlina' });
      const arr = Array.from(map.values());
      const priority = ['EUR','USD','GBP'];
      arr.sort((a,b)=>{
        const ia = priority.indexOf(a.isoMOEDA);
        const ib = priority.indexOf(b.isoMOEDA);
        if (ia !== -1 || ib !== -1) return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        return a.tipoMOEDA.localeCompare(b.tipoMOEDA);
      });
      return arr;
    };

    setCurrencies(ensureList(Array.isArray(currenciesData) ? currenciesData : []));
  }, [currenciesData]);

  // Mantém ref em sincronia com estado para usar no handler global
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Permite fechamento via evento customizado disparado pelo header
  useEffect(() => {
    const handler = () => setIsOpen(false);
    const node = ref.current;
    if (node) node.addEventListener('close-currency-dropdown', handler);
    return () => {
      if (node) node.removeEventListener('close-currency-dropdown', handler);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpenRef.current) return;
      const path = event.composedPath ? event.composedPath() : [];
      const targetInside = ref.current && (path.includes(ref.current) || ref.current.contains(event.target));
      if (!targetInside) {
        setIsOpen(false);
      }
    };
    // captura para evitar stopPropagation impedir fechamento
    document.addEventListener('pointerdown', handleClickOutside, true);
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside, true);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, []);

  const current = currencies.find(c => c.isoMOEDA === (currency || 'EUR'));

  return (
    <DropdownContainer className="notranslate" ref={ref}>
      <DropdownHeader type="button" $open={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <SymbolText>{current?.simboloMOEDA || '€'}</SymbolText>
        <span>{current?.isoMOEDA || currency || 'EUR'}</span>
        <Chevron $open={isOpen} />
      </DropdownHeader>
      {isOpen && (
        <DropdownList>
          {currencies.map((m) => {
            const selected = m.isoMOEDA === (current?.isoMOEDA || currency || 'EUR');
            return (
              <DropdownListItem
                key={m.isoMOEDA}
                $selected={selected}
                onClick={() => {
                  setCurrency(m.isoMOEDA);
                  setIsOpen(false);
                }}
              >
                <ItemSymbol>{m.simboloMOEDA}</ItemSymbol>
                <ItemText>
                  <ItemIso>{m.isoMOEDA}</ItemIso>
                  <ItemName>{m.tipoMOEDA}</ItemName>
                </ItemText>
                {selected && <CheckIcon />}
              </DropdownListItem>
            );
          })}
        </DropdownList>
      )}
    </DropdownContainer>
  );
};

export default CurrencyDropdown;
